import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Switch, FormControlLabel, TextField, Alert } from '@mui/material';
import { Mail, Clock, Users, CheckSquare, XCircle, RefreshCw } from 'lucide-react';
import AppointmentFeedbackService from '../../../services/appointmentFeedbackService';

function CheckoutFeedbackManager() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(5);
  const [feedback, setFeedback] = useState([]);
  const [lastChecked, setLastChecked] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check if already monitoring when component loads
    if (true) {
      // handleStartMonitoring();
    }
    return () => {
      if (isMonitoring) {
        AppointmentFeedbackService.stopMonitoring();
      }
    };
  }, []);

  const handleSubscribe = async () => {
    try {
      setStatus('loading');
      setMessage('Subscribing to checkout events...');
      await AppointmentFeedbackService.subscribeToCheckOutEvents();
      setIsSubscribed(true);
      setStatus('success');
      setMessage('Successfully subscribed to checkout events!');
    } catch (error) {
      console.error('Error subscribing to events:', error);
      setStatus('error');
      setMessage(`Failed to subscribe: ${error.message}`);
    }
  };

  const handleStartMonitoring = () => {
    try {
      AppointmentFeedbackService.startMonitoring(intervalMinutes);
      setIsMonitoring(true);
      setLastChecked(new Date().toLocaleString());
      localStorage.setItem('athena_feedback_monitoring', 'true');
      localStorage.setItem('athena_feedback_interval', intervalMinutes.toString());
      setStatus('success');
      setMessage(`Started monitoring for checkouts every ${intervalMinutes} minutes`);
    } catch (error) {
      console.error('Error starting monitoring:', error);
      setStatus('error');
      setMessage(`Failed to start monitoring: ${error.message}`);
    }
  };

  const handleStopMonitoring = () => {
    try {
      AppointmentFeedbackService.stopMonitoring();
      setIsMonitoring(false);
      localStorage.setItem('athena_feedback_monitoring', 'false');
      setStatus('success');
      setMessage('Stopped monitoring for checkouts');
    } catch (error) {
      console.error('Error stopping monitoring:', error);
      setStatus('error');
      setMessage(`Failed to stop monitoring: ${error.message}`);
    }
  };

  const handleIntervalChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setIntervalMinutes(value);
    }
  };

  const handleManualCheck = async () => {
    try {
      setStatus('loading');
      setMessage('Checking for recent checkouts...');
      await AppointmentFeedbackService.checkForChangedAppointments();
      setLastChecked(new Date().toLocaleString());
      setStatus('success');
      setMessage('Manual check completed successfully');
    } catch (error) {
      console.error('Error during manual check:', error);
      setStatus('error');
      setMessage(`Failed to check for checkouts: ${error.message}`);
    }
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckSquare size={20} />
            Athena Checkout Feedback
          </Typography>
          
          {isMonitoring ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Clock size={16} className="text-green-500" />
              <Typography variant="body2">Monitoring Active</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <XCircle size={16} className="text-gray-400" />
              <Typography variant="body2" color="text.secondary">Not Monitoring</Typography>
            </Box>
          )}
        </Box>
        
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Subscribe to Athena Health's checkout events and automatically send feedback links to patients.
        </Typography>
        
        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>
        )}
        
        {status === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>
        )}
        
        {status === 'loading' && (
          <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>
        )}
        
        <Box sx={{ mb: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubscribe}
            disabled={isSubscribed || status === 'loading'}
            sx={{ mr: 2, mb: 2 }}
          >
            Subscribe to Checkout Events
          </Button>
          
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={handleManualCheck}
            disabled={status === 'loading'}
            startIcon={<RefreshCw size={16} />}
            sx={{ mb: 2 }}
          >
            Check Now
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isMonitoring}
                onChange={isMonitoring ? handleStopMonitoring : handleStartMonitoring}
                disabled={!isSubscribed}
              />
            }
            label={isMonitoring ? "Monitoring Active" : "Start Monitoring"}
          />
          
          <TextField
            label="Check Interval (minutes)"
            type="number"
            value={intervalMinutes}
            onChange={handleIntervalChange}
            disabled={isMonitoring || !isSubscribed}
            variant="outlined"
            size="small"
            sx={{ ml: 2, width: 180 }}
            InputProps={{ inputProps: { min: 1, max: 60 } }}
          />
        </Box>
        
        {lastChecked && (
          <Typography variant="body2" color="text.secondary">
            Last checked: {lastChecked}
          </Typography>
        )}
        
        {isMonitoring && (
          <Alert severity="info" sx={{ mt: 2 }}>
            The system will check for newly checked-out appointments every {intervalMinutes} minutes and 
            automatically send feedback request emails to patients.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default CheckoutFeedbackManager;
